/* eslint-disable import/no-extraneous-dependencies */
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import QRCode from 'qrcode';
import { INfse } from '../hooks/useNfse';

// Logo oficial da NFS-e (horizontal, 1920x389 ~ proporção 4.936) servida em /public
const NFSE_LOGO_RATIO = 4.936;
let cachedNfseLogo: string | null = null;

// Carrega a logo já comprimida (downscale + JPEG) para deixar o PDF leve.
// A logo original tem ~99 KB; aqui cai para poucos KB, embutida uma única vez por nota.
const getNfseLogo = (): Promise<string> => {
  if (cachedNfseLogo !== null) return Promise.resolve(cachedNfseLogo);
  return new Promise<string>(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const targetW = 260;
      const targetH =
        Math.round((targetW * img.height) / img.width) || Math.round(targetW / NFSE_LOGO_RATIO);
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, targetW, targetH);
      ctx.drawImage(img, 0, 0, targetW, targetH);
      cachedNfseLogo = canvas.toDataURL('image/jpeg', 0.85);
      resolve(cachedNfseLogo);
    };
    img.onerror = () => {
      cachedNfseLogo = '';
      resolve('');
    };
    img.src = `${window.location.origin}/logo-nfse.png`;
  });
};

// Fontes exigidas pela NT 008/2026 (item 2.4): Arial nos títulos/labels e Microsoft Sans
// Serif nos conteúdos. Ficam em /public/fonts e só são baixadas na primeira exportação.
const DANFSE_FONTS = [
  { file: 'arial.ttf', family: 'Arial', style: 'normal' },
  { file: 'arialbd.ttf', family: 'Arial', style: 'bold' },
  { file: 'micross.ttf', family: 'MicrosoftSansSerif', style: 'normal' },
];
let cachedDanfseFonts: Promise<string[] | null> | null = null;

// Carrega as fontes em base64 (formato que o jsPDF recebe). Devolve null se alguma
// faltar — quem chama cai na Helvetica em vez de quebrar a exportação.
const loadDanfseFonts = (): Promise<string[] | null> => {
  if (!cachedDanfseFonts) {
    cachedDanfseFonts = Promise.all(
      DANFSE_FONTS.map(async font => {
        const res = await fetch(`${window.location.origin}/fonts/${font.file}`);
        if (!res.ok) throw new Error(`Fonte ${font.file} indisponível`);
        const bytes = new Uint8Array(await res.arrayBuffer());
        // Assinatura de TrueType: o dev server devolve o index.html para caminho inexistente
        if (bytes[0] !== 0 || bytes[1] !== 1 || bytes[2] !== 0 || bytes[3] !== 0) {
          throw new Error(`Fonte ${font.file} inválida`);
        }
        let binary = '';
        for (let i = 0; i < bytes.length; i += 0x8000) {
          binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + 0x8000)));
        }
        return btoa(binary);
      }),
    ).catch(() => null);
  }
  return cachedDanfseFonts;
};

// Função auxiliar para formatar nome de município (Title Case)
const formatMunicipio = (nome: string | undefined | null): string => {
  if (!nome) return '';
  return nome
    .toLowerCase()
    .split(' ')
    .map(word => {
      const lowerWords = ['de', 'da', 'do', 'das', 'dos', 'e'];
      if (lowerWords.includes(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

// Função auxiliar para formatar CNPJ/CPF
const formatCnpjCpf = (value: string | undefined): string => {
  if (!value) return '-';
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 14) {
    return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  if (cleaned.length === 11) {
    return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return value;
};

// Função auxiliar para formatar CEP (NT 008/2026: nn.nnn-nnn)
const formatCep = (value: string | undefined): string => {
  if (!value) return '-';
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 8) {
    return cleaned.replace(/^(\d{2})(\d{3})(\d{3})$/, '$1.$2-$3');
  }
  return value;
};

// Função auxiliar para formatar moeda
const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

// Função auxiliar para formatar percentual (2 casas)
const formatPercent = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  return `${Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

// Função auxiliar para formatar data (DD/MM/AAAA). Data sem hora é montada à mão:
// new Date('2026-09-01') é UTC e viraria 31/08 no fuso do Brasil.
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  if (partes) return `${partes[3]}/${partes[2]}/${partes[1]}`;
  return new Date(dateString).toLocaleDateString('pt-BR');
};

// Função auxiliar para formatar data e hora (DD/MM/AAAA hh:mm:ss)
const formatDateTime = (dateString: string | undefined | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR')}`;
};

// Data e hora como estão no XML (dados_completos guarda o texto original, com o fuso
// de quem emitiu). Sem o texto original, cai na coluna, convertida para o fuso do navegador.
const formatDateTimeXml = (
  original: string | undefined | null,
  coluna: string | undefined | null,
): string => {
  const partes = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(original || '');
  if (partes) {
    return `${partes[3]}/${partes[2]}/${partes[1]} ${partes[4]}:${partes[5]}:${partes[6]}`;
  }
  return formatDateTime(coluna);
};

// Valor monetário que vem como texto do XML (ex.: "5.00")
const formatCurrencyXml = (value: string | undefined | null): string => {
  if (value === undefined || value === null || value === '' || Number.isNaN(Number(value))) {
    return '-';
  }
  return formatCurrency(Number(value));
};

// Percentual que vem como texto do XML (ex.: "0.10")
const formatPercentXml = (value: string | undefined | null): string => {
  if (value === undefined || value === null || value === '' || Number.isNaN(Number(value))) {
    return '-';
  }
  return formatPercent(Number(value));
};

// Campos concatenados por barra; se nenhum tem valor, sai um traço só
const joinCampos = (partes: string[]): string =>
  partes.every(parte => parte === '-') ? '-' : partes.join(' / ');

// Sigla da UF pelos 2 primeiros dígitos do código IBGE do município
const UF_POR_CODIGO_IBGE: Record<string, string> = {
  '11': 'RO',
  '12': 'AC',
  '13': 'AM',
  '14': 'RR',
  '15': 'PA',
  '16': 'AP',
  '17': 'TO',
  '21': 'MA',
  '22': 'PI',
  '23': 'CE',
  '24': 'RN',
  '25': 'PB',
  '26': 'PE',
  '27': 'AL',
  '28': 'SE',
  '29': 'BA',
  '31': 'MG',
  '32': 'ES',
  '33': 'RJ',
  '35': 'SP',
  '41': 'PR',
  '42': 'SC',
  '43': 'RS',
  '50': 'MS',
  '51': 'MT',
  '52': 'GO',
  '53': 'DF',
};

// "Município / UF / País" a partir do nome, do código IBGE e do país (ISO, 2 letras)
const formatLocal = (
  nome: string | undefined,
  codigoIbge: string | undefined,
  pais: string | undefined,
): string => {
  const uf = UF_POR_CODIGO_IBGE[(codigoIbge || '').slice(0, 2)];
  if (!nome) return pais || '-';
  if (!uf) return pais ? `${nome} / ${pais}` : nome;
  return `${nome} / ${uf} / ${pais || 'BR'}`;
};

// Código de tributação nacional (cTribNac): nn.nn.nn
const formatCodTribNac = (codigo: string | undefined): string => {
  if (!codigo) return '-';
  const digitos = codigo.replace(/\D/g, '');
  if (digitos.length === 6) return digitos.replace(/^(\d{2})(\d{2})(\d{2})$/, '$1.$2.$3');
  return codigo;
};

// Código da NBS: n.nnnn.nn.nn
const formatNbs = (codigo: string | undefined): string => {
  if (!codigo) return '-';
  const digitos = codigo.replace(/\D/g, '');
  if (digitos.length === 9) {
    return digitos.replace(/^(\d)(\d{4})(\d{2})(\d{2})$/, '$1.$2.$3.$4');
  }
  return codigo;
};

// Chave de acesso: impressa em bloco único de 50 dígitos (NT 008/2026, item 2.1.1)
const formatChaveAcesso = (chave: string | null | undefined): string => {
  if (!chave) return '-';
  return chave.replace(/\s+/g, '').replace(/^NFS/i, '');
};

// Dados de destinatário / intermediário dentro de dados_completos.danfse
type PessoaExtra = {
  cnpj?: string;
  cpf?: string;
  nif?: string;
  im?: string;
  nome?: string;
  fone?: string;
  email?: string;
  municipio?: string;
  nome_municipio?: string;
  uf?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
};

// Bloco "Tributação IBS / CBS" (dados_completos.danfse.ibscbs)
type IbsCbsExtras = {
  cst?: string;
  c_class_trib?: string;
  c_ind_op?: string;
  c_localidade_incid?: string;
  x_localidade_incid?: string;
  v_calc_ree_rep_res?: string;
  v_bc?: string;
  p_red_aliq_uf?: string;
  p_red_aliq_mun?: string;
  p_red_aliq_cbs?: string;
  p_ibs_uf?: string;
  p_ibs_mun?: string;
  p_aliq_efet_uf?: string;
  p_aliq_efet_mun?: string;
  p_cbs?: string;
  p_aliq_efet_cbs?: string;
  v_ibs_uf?: string;
  v_ibs_mun?: string;
  v_ibs_tot?: string;
  v_cbs?: string;
  v_tot_nf?: string;
};

// Campos que só o DANFSe usa. O extrator grava em dados_completos.danfse, com os códigos
// crus do XML; notas extraídas antes disso não têm o bloco, então tudo aqui é opcional.
type DanfseExtras = {
  tp_amb?: string;
  tp_emit?: string;
  fin_nfse?: string;
  ind_dest?: string;
  ch_substda?: string;
  x_trib_mun?: string;
  x_out_inf?: string;
  c_loc_incid?: string;
  c_pais_prestacao?: string;
  c_pais_result?: string;
  op_simp_nac?: string;
  reg_ap_trib_sn?: string;
  reg_esp_trib?: string;
  tp_imunidade?: string;
  tp_susp?: string;
  n_processo?: string;
  tp_bm?: string;
  v_calc_bm?: string;
  v_red_bc_bm?: string;
  v_desc_incond?: string;
  v_desc_cond?: string;
  c_obra?: string;
  insc_imob_fisc?: string;
  id_atv_evt?: string;
  id_doc_tec?: string;
  doc_ref?: string;
  x_ped?: string;
  x_item_ped?: string;
  v_tot_trib_fed?: string;
  v_tot_trib_est?: string;
  v_tot_trib_mun?: string;
  p_tot_trib_sn?: string;
  intermediario?: PessoaExtra;
  destinatario?: PessoaExtra;
  ibscbs?: IbsCbsExtras;
};

const getExtras = (nota: INfse): DanfseExtras => nota.dados_completos?.danfse || {};

// Ambiente gerador (ambGer): 1 = Prefeitura, 2 = Sistema Nacional da NFS-e.
// É o que a coluna `ambiente` guarda; na falta dela, é o 8º dígito da chave de acesso.
const getAmbienteGeradorLabel = (nota: INfse): string => {
  const informado = (nota.ambiente || '').toString().trim();
  const chave = (nota.chave_nfse || '').replace(/\D/g, '');
  const codigo = /^[12]$/.test(informado) ? informado : (chave.length === 50 && chave[7]) || '';
  if (codigo === '1') return 'Prefeitura';
  if (codigo === '2') return 'Sistema Nacional da NFS-e';
  return '-';
};

// Homologação é o tpAmb = 2 do DPS. A coluna `ambiente` é o ambGer (2 = Sistema Nacional),
// então o código numérico dela NÃO indica homologação — só o tpAmb ou o texto.
const isNotaHomologacao = (nota: INfse): boolean =>
  getExtras(nota).tp_amb === '2' || /homolog/i.test(nota.ambiente || '');

// Emitente da NFS-e (tpEmit). Sem o código, é deduzido de quem aparece como emissor da nota
const getEmitenteLabel = (nota: INfse): string => {
  const tpEmit = getExtras(nota).tp_emit;
  if (tpEmit === '1') return 'Prestador';
  if (tpEmit === '2') return 'Tomador';
  if (tpEmit === '3') return 'Intermediário';
  const digitos = (valor: string | undefined) => (valor || '').replace(/\D/g, '');
  const emissor = digitos(nota.emissor_cnpj || nota.emissor_cpf);
  const prestador = digitos(nota.prestador_cnpj || nota.prestador_cpf);
  if (!emissor && !prestador) return '-';
  if (!emissor || !prestador || emissor === prestador) return 'Prestador';
  if (emissor === digitos(nota.tomador_cnpj || nota.tomador_cpf)) return 'Tomador';
  return 'Intermediário';
};

// Situação da NFS-e (cStat)
const getSituacaoLabel = (status: string | undefined): string => {
  switch (status) {
    case '100':
      return 'NFS-e Gerada';
    case '101':
      return 'NFS-e de Substituição Gerada';
    case '102':
      return 'NFS-e de Decisão Judicial ou Administrativa';
    case '103':
      return 'NFS-e Avulsa';
    default:
      return status || '-';
  }
};

// Finalidade da NFS-e (finNFSe)
const getFinalidadeLabel = (codigo: string | undefined): string => {
  if (codigo === '0') return 'NFS-e regular';
  return codigo || '-';
};

// Simples Nacional na data de competência (opSimpNac)
const getSimplesNacionalLabel = (codigo: string | undefined): string => {
  switch (codigo) {
    case '1':
      return 'Não Optante';
    case '2':
      return 'Optante - Microempreendedor Individual (MEI)';
    case '3':
      return 'Optante - Microempresa ou Empresa de Pequeno Porte (ME/EPP)';
    default:
      return codigo || '-';
  }
};

// Regime de apuração tributária pelo Simples Nacional (regApTribSN)
const getRegimeApuracaoSnLabel = (codigo: string | undefined): string => {
  switch (codigo) {
    case '1':
      return 'Regime de apuração dos tributos federais e municipal pelo Simples Nacional';
    case '2':
      return 'Regime de apuração dos tributos federais pelo SN e o ISSQN pela NFS-e conforme respectiva legislação municipal';
    case '3':
      return 'Regime de apuração dos tributos federais e municipal pela NFS-e conforme respectivas legislações de cada tributo';
    default:
      return codigo || '-';
  }
};

// Regime especial de tributação do ISSQN (regEspTrib)
const getRegimeEspecialLabel = (codigo: string | undefined): string => {
  switch (codigo) {
    case '0':
      return 'Nenhum';
    case '1':
      return 'Ato Cooperado (Cooperativa)';
    case '2':
      return 'Estimativa';
    case '3':
      return 'Microempresa Municipal';
    case '4':
      return 'Notário ou Registrador';
    case '5':
      return 'Profissional Autônomo';
    case '6':
      return 'Sociedade de Profissionais';
    case '9':
      return 'Outros';
    default:
      return codigo || '-';
  }
};

// Tipo de imunidade do ISSQN (tpImunidade)
const getImunidadeLabel = (codigo: string | undefined): string => {
  switch (codigo) {
    case '1':
      return 'Patrimônio, renda ou serviços, uns dos outros';
    case '2':
      return 'Templos de qualquer culto';
    case '3':
      return 'Patrimônio, renda ou serviços dos partidos políticos, sindicatos e instituições';
    case '4':
      return 'Livros, jornais, periódicos e o papel destinado a sua impressão';
    case '5':
      return 'Fonogramas e videofonogramas musicais produzidos no Brasil';
    default:
      return codigo || '-';
  }
};

// Suspensão da exigibilidade do ISSQN (tpSusp)
const getSuspensaoLabel = (codigo: string | undefined): string => {
  if (codigo === '1') return 'Exigibilidade Suspensa por Decisão Judicial';
  if (codigo === '2') return 'Exigibilidade Suspensa por Processo Administrativo';
  return codigo || '-';
};

// Benefício municipal (tpBM)
const getBeneficioMunicipalLabel = (codigo: string | undefined): string => {
  switch (codigo) {
    case '1':
      return 'Isenção';
    case '2':
    case '3':
      return 'Redução da Base de Cálculo';
    case '4':
      return 'Alíquota Diferenciada';
    default:
      return codigo || '-';
  }
};

// Tipo de tributação do ISSQN (tribISSQN)
const getTribIssqnLabel = (codigo: string | undefined): string => {
  switch (codigo) {
    case '1':
      return 'Operação Tributável';
    case '2':
      return 'Imunidade';
    case '3':
      return 'Exportação de Serviço';
    case '4':
      return 'Não Incidência';
    default:
      return codigo || '-';
  }
};

// Retenção do ISSQN (tpRetISSQN)
const getTipoRetencaoIssqnLabel = (tipo: string | undefined): string => {
  switch (tipo) {
    case '1':
      return 'Não Retido';
    case '2':
      return 'Retido pelo Tomador';
    case '3':
      return 'Retido pelo Intermediário';
    default:
      return tipo || '-';
  }
};

// Descrição das contribuições sociais retidas (tpRetPisCofins, tabela da NT 007/2026)
const getRetPisCofinsDescricao = (tipo: string | undefined | null): string => {
  switch (tipo) {
    case '0':
      return 'PIS/COFINS/CSLL Não Retidos';
    case '1':
      return 'PIS/COFINS Retido';
    case '2':
      return 'PIS/COFINS Não Retido';
    case '3':
      return 'PIS/COFINS/CSLL Retidos';
    case '4':
      return 'PIS/COFINS Retidos, CSLL Não Retido';
    case '5':
      return 'PIS Retido, COFINS/CSLL Não Retido';
    case '6':
      return 'COFINS Retido, PIS/CSLL Não Retido';
    case '7':
      return 'COFINS/CSLL Retidos, PIS Não Retido';
    case '8':
      return 'CSLL Retido, PIS/COFINS Não Retido';
    case '9':
      return 'PIS/CSLL Retidos, COFINS Não Retido';
    default:
      return tipo || '-';
  }
};

// Detecta se a nota foi substituída (cancelada com chave substituta + motivo de substituição)
const isSubstituida = (nota: INfse): boolean =>
  !!nota.chave_substituta && (nota.motivo_cancelamento || '').toLowerCase().includes('substitu');

// Cache de QR Codes já gerados (por chave)
const qrCodeCache = new Map<string, string>();

// Gera o QR Code (dataURL PNG) apontando para a consulta pública nacional da NFS-e
const getQrCodeDataUrl = async (chave: string | null | undefined): Promise<string> => {
  if (!chave) return '';
  if (qrCodeCache.has(chave)) return qrCodeCache.get(chave) as string;
  try {
    const url = `https://www.nfse.gov.br/ConsultaPublica/?tpc=1&chave=${chave}`;
    const dataUrl = await QRCode.toDataURL(url, {
      margin: 1,
      width: 160,
      errorCorrectionLevel: 'M',
    });
    qrCodeCache.set(chave, dataUrl);
    return dataUrl;
  } catch {
    return '';
  }
};

// Desenha a marca d'água diagonal em Arial normal, cinza K35 (NT 008/2026, itens 2.5.1/2.5.2).
// É chamada antes do conteúdo, para ficar por baixo dos campos sem precisar de transparência.
const drawWatermark = (doc: jsPDF, label: string, font: string) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setTextColor(166, 166, 166); // cinza K35
  doc.setFont(font, 'normal');
  doc.setFontSize(60);
  // Centraliza na página: o texto sobe a 45° a partir do ponto inicial
  const half = doc.getTextWidth(label) / 2 / Math.SQRT2;
  doc.text(label, pageWidth / 2 - half, pageHeight / 2 + half, { angle: 45 });
  doc.setTextColor(0, 0, 0);
};

type Field = {
  l: string;
  v?: string | number | null;
  span?: number;
  caps?: boolean; // label em caixa alta, 7pt (bloco de identificação e valores em destaque)
  shade?: boolean;
};

type Pessoa = {
  doc: string;
  im: string;
  fone: string;
  nome: string;
  mun: string;
  ibgeCep: string;
  end: string;
  email: string;
};

// Renderiza o DANFSe seguindo o modelo do Anexo I da NT 008/2026 (pág. 25) e as posições
// do item 2.4.5: grade de 4 colunas de 5,09 cm, título do bloco na 1ª coluna, linhas de
// 0,5pt só entre os blocos, borda de 1pt e página única.
// Usa apenas os dados já presentes na nota (capturados do XML) — não emite a NFS-e.
const renderDanfseDocument = async (nota: INfse): Promise<{ doc: jsPDF; fileName: string }> => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 3; // quadro a 0,30 cm da borda do papel
  const W = pageW - 2 * M; // 20,40 cm
  const COL = W / 4;
  const PAD = 1.6;
  const BOTTOM = pageH - M;
  const ROW = 6.45; // linhas de campos (~0,63 cm)
  const ROW_ID = 6.9; // linhas do bloco de identificação e de valor total (~0,67 cm)
  const LH = 2.9; // entrelinha do conteúdo em 7pt
  const GRAY = 242; // cinza claro 5%
  let y = M;

  // Sem os arquivos das fontes o documento sai em Helvetica, em vez de falhar
  let fLabel = 'helvetica';
  let fText = 'helvetica';
  const fontes = await loadDanfseFonts();
  if (fontes) {
    try {
      DANFSE_FONTS.forEach((font, i) => {
        doc.addFileToVFS(font.file, fontes[i]);
        doc.addFont(font.file, font.family, font.style);
      });
      fLabel = 'Arial';
      fText = 'MicrosoftSansSerif';
    } catch {
      fLabel = 'helvetica';
      fText = 'helvetica';
    }
  }

  // Marca d'água para canceladas / substituídas
  if (nota.cancelada) {
    drawWatermark(doc, isSubstituida(nota) ? 'SUBSTITUÍDA' : 'CANCELADA', fLabel);
  }

  const colX = (col: number) => M + col * COL;

  const shade = (x: number, yy: number, w: number, h: number) => {
    doc.setFillColor(GRAY, GRAY, GRAY);
    doc.rect(x, yy, w, h, 'F');
  };

  const hline = (yy: number) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.176); // 0,5pt
    doc.line(M, yy, M + W, yy);
  };

  // Corta o texto com reticências quando não cabe na largura do campo
  const fit = (text: string, maxW: number): string => {
    if (doc.getTextWidth(text) <= maxW) return text;
    let cut = text;
    while (cut.length > 1 && doc.getTextWidth(`${cut}...`) > maxW) cut = cut.slice(0, -1);
    return `${cut.trimEnd()}...`;
  };

  // Limita a quantidade de linhas, marcando o corte com reticências
  const clip = (lines: string[], max: number): string[] => {
    if (lines.length <= max) return lines;
    const kept = lines.slice(0, Math.max(max, 0));
    if (kept.length) kept[kept.length - 1] = `${kept[kept.length - 1].slice(0, -3).trimEnd()}...`;
    return kept;
  };

  const field = (col: number, yy: number, h: number, f: Field) => {
    const x = colX(col);
    const w = COL * (f.span || 1);
    if (f.shade) shade(x, yy, w, h);
    doc.setTextColor(0, 0, 0);
    doc.setFont(fLabel, 'bold');
    doc.setFontSize(f.caps ? 7 : 6);
    doc.text(f.l, x + PAD, yy + (f.caps ? 2.9 : 2.5));
    // Campo sem informação no XML sai com traço (item 2.4.5, nota 12)
    const value = f.v === undefined || f.v === null || f.v === '' ? '-' : String(f.v);
    doc.setFont(fText, 'normal');
    doc.setFontSize(7);
    doc.text(fit(value, w - 2 * PAD), x + PAD, yy + h - 1.3);
  };

  const row = (h: number, fields: Field[], startCol = 0) => {
    let col = startCol;
    fields.forEach(f => {
      field(col, y, h, f);
      col += f.span || 1;
    });
    y += h;
  };

  // Título do bloco: ocupa a 1ª coluna da 1ª linha do bloco, com fundo cinza
  const blockTitle = (text: string, h: number, w = COL) => {
    shade(M, y, w, h);
    hline(y);
    doc.setTextColor(0, 0, 0);
    doc.setFont(fLabel, 'bold');
    doc.setFontSize(7);
    doc.text(text, M + PAD, y + 3.1);
  };

  // Bloco suprimido: só a mensagem centralizada, com 0,32 cm (item 2.4.5, notas 2 e 4)
  const suprimido = (msg: string) => {
    hline(y);
    doc.setTextColor(0, 0, 0);
    doc.setFont(fText, 'normal');
    doc.setFontSize(7);
    doc.text(msg, pageW / 2, y + 2.45, { align: 'center' });
    y += 3.2;
  };

  // ==================== CABEÇALHO ====================
  const extras = getExtras(nota);
  const isHomologacao = isNotaHomologacao(nota);
  const headH = 11.8;
  shade(M, y, W, headH);

  const logo = await getNfseLogo();
  if (logo) {
    const lw = 40;
    doc.addImage(logo, 'JPEG', M + 1.9, y + 1.9, lw, lw / NFSE_LOGO_RATIO, undefined, 'FAST');
  }

  const centerX = colX(1) + COL;
  const headLines = ['DANFSe v2.0', 'Documento Auxiliar da NFS-e'];
  const headY = isHomologacao ? y + 3.7 : y + 5.2;
  doc.setTextColor(0, 0, 0);
  doc.setFont(fLabel, 'bold');
  doc.setFontSize(9);
  headLines.forEach((line, i) => doc.text(line, centerX, headY + i * 3.5, { align: 'center' }));
  if (isHomologacao) {
    doc.setTextColor(255, 0, 0);
    doc.text('NFS-e SEM VALIDADE JURÍDICA', centerX, headY + 7, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }

  // Município do emitente não é exibido quando o item do código de tributação nacional é 99
  const emitenteUf = nota.emissor_uf || nota.prestador_uf || '';
  const emitenteMun = formatMunicipio(
    nota.emissor_nome_municipio || nota.prestador_nome_municipio || nota.local_emissao,
  );
  const itemTribNac = (nota.servico_codigo || '').replace(/\D/g, '').slice(0, 2);
  const rx = colX(3) + PAD;
  doc.setFont(fText, 'normal');
  if (emitenteMun && itemTribNac !== '99') {
    doc.setFontSize(8);
    const munLines = doc.splitTextToSize(
      `Município: ${emitenteMun}${emitenteUf ? ` / ${emitenteUf}` : ''}`,
      COL - 2 * PAD,
    );
    doc.text(clip(munLines, 2), rx, y + 3, { lineHeightFactor: 1.05 });
  }
  doc.setFontSize(6);
  doc.text(`Ambiente Gerador: ${getAmbienteGeradorLabel(nota)}`, rx, y + 8.7);
  doc.text(`Tipo de Ambiente: ${isHomologacao ? 'Homologação' : 'Produção'}`, rx, y + 11);
  y += headH;

  // ==================== IDENTIFICAÇÃO + QR ====================
  hline(y);
  const idTop = y;
  let situacao = getSituacaoLabel(nota.status);
  if (nota.cancelada) situacao = isSubstituida(nota) ? 'Substituída' : 'Cancelada';

  row(7.9, [
    { l: 'CHAVE DE ACESSO DA NFS-E', v: formatChaveAcesso(nota.chave_nfse), span: 3, caps: true },
  ]);
  row(ROW_ID, [
    { l: 'NÚMERO DA NFS-E', v: nota.numero_nfse, caps: true },
    { l: 'COMPETÊNCIA DA NFS-E', v: formatDate(nota.data_competencia), caps: true },
    {
      l: 'DATA E HORA DA EMISSÃO DA NFS-E',
      v: formatDateTimeXml(nota.dados_completos?.dh_processamento, nota.dh_processamento),
      caps: true,
    },
  ]);
  row(ROW_ID, [
    { l: 'NÚMERO DA DPS', v: nota.numero_dps, caps: true },
    { l: 'SÉRIE DA DPS', v: nota.serie, caps: true },
    {
      l: 'DATA E HORA DA EMISSÃO DA DPS',
      v: formatDateTimeXml(nota.dados_completos?.data_emissao, nota.data_emissao),
      caps: true,
    },
  ]);
  row(ROW_ID, [
    { l: 'EMITENTE DA NFS-e', v: getEmitenteLabel(nota), caps: true, shade: true },
    { l: 'SITUAÇÃO DA NFS-e', v: situacao, caps: true },
    { l: 'FINALIDADE', v: getFinalidadeLabel(extras.fin_nfse), caps: true },
  ]);

  // QR Code (1,52 x 1,52 cm) e a descrição complementar em 3 linhas de 6pt
  const qrData = await getQrCodeDataUrl(nota.chave_nfse);
  if (qrData) {
    const qrSize = 15.2;
    doc.addImage(qrData, 'PNG', colX(3) + (COL - qrSize) / 2, idTop + 1.9, qrSize, qrSize);
    doc.setFont(fText, 'normal');
    doc.setFontSize(6);
    const cap = doc.splitTextToSize(
      'A autenticidade desta NFS-e pode ser verificada pela leitura deste código QR ou pela consulta da chave de acesso no portal nacional da NFS-e',
      COL - 2 * PAD,
    );
    doc.text(cap, rx, idTop + 20.3, { lineHeightFactor: 1.08 });
  }

  // ==================== PRESTADOR / TOMADOR ====================
  // O bloco do destinatário não tem o campo de inscrição municipal (comIm = false)
  const pessoa = (titulo: string, p: Pessoa, comIm = true) => {
    blockTitle(titulo, ROW);
    if (!comIm) field(3, y, ROW, { l: 'Telefone', v: p.fone });
    row(
      ROW,
      comIm
        ? [
            { l: 'CNPJ / CPF / NIF', v: p.doc },
            { l: 'Indicador Municipal (Inscrição)', v: p.im },
            { l: 'Telefone', v: p.fone },
          ]
        : [{ l: 'CNPJ / CPF / NIF', v: p.doc }],
      1,
    );
    row(ROW, [
      { l: 'Nome / Nome Empresarial', v: p.nome, span: 2 },
      { l: 'Município / Sigla UF', v: p.mun },
      { l: 'Código IBGE / CEP', v: p.ibgeCep },
    ]);
    row(ROW, [
      { l: 'Endereço', v: p.end, span: 2 },
      { l: 'E-mail', v: p.email, span: 2 },
    ]);
  };

  const enderecoOf = (parts: (string | undefined)[]) => parts.filter(Boolean).join(', ');
  // O endereço do tomador/destinatário no XML não traz a UF: sai do código IBGE do município
  const municipioUf = (
    nome: string | undefined,
    ufInformada: string | undefined,
    codigoIbge?: string,
  ) => {
    const uf = ufInformada || UF_POR_CODIGO_IBGE[(codigoIbge || '').slice(0, 2)];
    return nome ? `${formatMunicipio(nome)}${uf ? ` / ${uf}` : ''}` : '';
  };
  const ibgeCep = (ibge: string | undefined, cep: string | undefined) =>
    ibge || cep ? `${ibge || '-'} / ${formatCep(cep)}` : '';

  const pessoaExtra = (p: PessoaExtra): Pessoa => ({
    doc: p.cnpj || p.cpf ? formatCnpjCpf(p.cnpj || p.cpf) : p.nif || '',
    im: p.im || '',
    fone: p.fone || '',
    nome: p.nome || '',
    mun: municipioUf(p.nome_municipio, p.uf, p.municipio),
    ibgeCep: ibgeCep(p.municipio, p.cep),
    end: enderecoOf([p.logradouro, p.numero, p.complemento, p.bairro]),
    email: p.email || '',
  });

  pessoa('PRESTADOR / FORNECEDOR', {
    doc: formatCnpjCpf(
      nota.prestador_cnpj || nota.prestador_cpf || nota.emissor_cnpj || nota.emissor_cpf,
    ),
    im: nota.prestador_im || nota.emissor_im || '',
    fone: nota.prestador_fone || nota.emissor_fone || '',
    nome: nota.prestador_nome || nota.emissor_nome || '',
    mun: municipioUf(
      nota.prestador_nome_municipio || nota.emissor_nome_municipio,
      nota.prestador_uf || nota.emissor_uf,
      nota.prestador_municipio || nota.emissor_municipio,
    ),
    ibgeCep: ibgeCep(
      nota.prestador_municipio || nota.emissor_municipio,
      nota.prestador_cep || nota.emissor_cep,
    ),
    end: enderecoOf([
      nota.prestador_logradouro || nota.emissor_logradouro,
      nota.prestador_numero || nota.emissor_numero,
      nota.prestador_complemento || nota.emissor_complemento,
      nota.prestador_bairro || nota.emissor_bairro,
    ]),
    email: nota.prestador_email || nota.emissor_email || '',
  });
  row(ROW, [
    {
      l: 'Simples Nacional na Data de Competência',
      v: getSimplesNacionalLabel(extras.op_simp_nac),
    },
    {
      l: 'Regime de Apuração Tributária pelo SN',
      v: getRegimeApuracaoSnLabel(extras.reg_ap_trib_sn),
      span: 2,
    },
  ]);

  if (nota.tomador_nome || nota.tomador_cnpj || nota.tomador_cpf) {
    pessoa('TOMADOR / ADQUIRENTE', {
      doc: formatCnpjCpf(nota.tomador_cnpj || nota.tomador_cpf),
      im: nota.tomador_im || '',
      fone: nota.tomador_fone || '',
      nome: nota.tomador_nome || '',
      mun: municipioUf(nota.tomador_nome_municipio, nota.tomador_uf, nota.tomador_municipio),
      ibgeCep: ibgeCep(nota.tomador_municipio, nota.tomador_cep),
      end: enderecoOf([
        nota.tomador_logradouro,
        nota.tomador_numero,
        nota.tomador_complemento,
        nota.tomador_bairro,
      ]),
      email: nota.tomador_email || '',
    });
  } else {
    suprimido('TOMADOR/ADQUIRENTE DA OPERAÇÃO NÃO IDENTIFICADO NA NFS-e');
  }

  // Destinatário e intermediário: bloco completo quando a nota traz, senão suprimido (item 2.3.1)
  if (extras.destinatario) {
    pessoa('DESTINATÁRIO DA OPERAÇÃO', pessoaExtra(extras.destinatario), false);
  } else if (extras.ind_dest === '0') {
    // indDest = 0: o destinatário é o próprio tomador (item 2.3.2)
    suprimido('O DESTINATÁRIO É O PRÓPRIO TOMADOR/ADQUIRENTE DA OPERAÇÃO');
  } else {
    suprimido('DESTINATÁRIO DA OPERAÇÃO NÃO IDENTIFICADO NA NFS-e');
  }
  if (extras.intermediario) {
    pessoa('INTERMEDIÁRIO DA OPERAÇÃO', pessoaExtra(extras.intermediario));
  } else {
    suprimido('INTERMEDIÁRIO DA OPERAÇÃO NÃO IDENTIFICADO NA NFS-e');
  }

  // ==================== SERVIÇO PRESTADO ====================
  blockTitle('SERVIÇO PRESTADO', ROW);
  row(
    ROW,
    [
      {
        l: 'Código de Tributação Nacional / Municipal',
        v: `${formatCodTribNac(nota.servico_codigo)} / ${nota.servico_codigo_trib_mun || '-'}`,
      },
      { l: 'Código da NBS', v: formatNbs(nota.servico_codigo_nbs) },
      {
        l: 'Local da Prestação / Sigla UF / País',
        v: formatLocal(nota.local_prestacao, nota.servico_local_prestacao, extras.c_pais_prestacao),
      },
    ],
    1,
  );

  // Descrição do código de tributação: campo sem título (label) no DANFSe.
  // A descrição municipal, quando existe, tem prioridade sobre a nacional.
  doc.setFont(fText, 'normal');
  doc.setFontSize(7);
  doc.text(fit(extras.x_trib_mun || nota.tributo_nacional || '-', W - 2 * PAD), M + PAD, y + 2.8);
  y += 4;

  // As linhas ** do ISSQN (regime especial/imunidade/suspensão e benefício municipal) e a
  // linha *** de PIS/COFINS são suprimidas quando não se aplicam; o espaço que sobra, junto
  // com o do canhoto (opcional), vai para a descrição do serviço e as informações complementares.
  // regEspTrib = 0 é "Nenhum": sozinho não justifica imprimir a linha
  const temRegimes = !!(
    (extras.reg_esp_trib && extras.reg_esp_trib !== '0') ||
    extras.tp_imunidade ||
    extras.tp_susp ||
    extras.n_processo
  );
  const calculoBm = extras.v_calc_bm || extras.v_red_bc_bm;
  const temDeducoes = !!(nota.valor_calc_dr || extras.tp_bm || calculoBm || extras.v_desc_incond);
  const anoCompetencia = Number((nota.data_competencia || '').slice(0, 4));
  const temPisCofins = !anoCompetencia || anoCompetencia <= 2026;
  const infoTitleH = 4.1;
  const alturaFixa =
    (2 + (temRegimes ? 1 : 0) + (temDeducoes ? 1 : 0)) * ROW +
    (temPisCofins ? 2 : 1) * ROW +
    4 * ROW +
    2 * ROW_ID +
    infoTitleH;
  const disponivel = BOTTOM - y - alturaFixa;

  // Totais aproximados: em valor (vTotTrib) quando a nota traz, senão em percentual
  const totaisEmValor = !!(extras.v_tot_trib_fed || extras.v_tot_trib_est || extras.v_tot_trib_mun);
  const totais = totaisEmValor
    ? [extras.v_tot_trib_fed, extras.v_tot_trib_est, extras.v_tot_trib_mun].map(formatCurrencyXml)
    : [nota.p_tot_trib_fed, nota.p_tot_trib_est, nota.p_tot_trib_mun].map(formatPercent);
  const totaisTributos = `Totais Aproximados dos Tributos cfe. Lei nº 12.741/2012: Federais: ${totais[0]} ; Estaduais: ${totais[1]} ; Municipais: ${totais[2]}`;
  const outrasInformacoes = nota.outras_informacoes || extras.x_out_inf;
  // Ordem fixada pela NT 008/2026 (item 2.4.5), separada por pipes
  const infoTexto = [
    nota.servico_info_complementar && `Inf. Cont.: ${nota.servico_info_complementar}`,
    extras.ch_substda && `NFS-e Subst.: ${extras.ch_substda}`,
    extras.doc_ref && `Doc. Ref.: ${extras.doc_ref}`,
    extras.c_obra && `Cod. Obra: ${extras.c_obra}`,
    extras.insc_imob_fisc && `Insc. Imob.: ${extras.insc_imob_fisc}`,
    extras.id_atv_evt && `Cod. Evt.: ${extras.id_atv_evt}`,
    extras.id_doc_tec && `Doc. Tec.: ${extras.id_doc_tec}`,
    extras.x_ped && `Núm. Ped.: ${extras.x_ped}`,
    extras.x_item_ped && `Item Ped.: ${extras.x_item_ped}`,
    outrasInformacoes && `Inf. A. T. Mun.: ${outrasInformacoes}`,
  ]
    .filter(Boolean)
    .join(' | ');

  const descricao = nota.servico_descricao || '-';
  const descLinesAll: string[] = doc.splitTextToSize(
    descricao.length > 1300 ? `${descricao.slice(0, 1297)}...` : descricao,
    W - 2 * PAD,
  );
  const infoLinesAll: string[] = infoTexto
    ? doc.splitTextToSize(
        infoTexto.length > 2000 ? `${infoTexto.slice(0, 1997)}...` : infoTexto,
        W - 2 * PAD,
      )
    : [];

  const descTop = 5.4; // 1ª linha do conteúdo, abaixo do label
  const descPrecisa = Math.max(ROW, descTop + (descLinesAll.length - 1) * LH + 1.3);
  const infoPrecisa = 3 + infoLinesAll.length * LH + 1.3; // + linha fixa dos totais
  const infoReservado = Math.min(infoPrecisa, disponivel / 2);
  const descH = Math.min(descPrecisa, disponivel - infoReservado);
  const infoH = disponivel - descH;

  doc.setFont(fLabel, 'bold');
  doc.setFontSize(6);
  doc.text('Descrição do Serviço', M + PAD, y + 2.5);
  doc.setFont(fText, 'normal');
  doc.setFontSize(7);
  const descMax = Math.floor((descH - descTop - 0.6) / LH) + 1;
  clip(descLinesAll, descMax).forEach((line, i) => doc.text(line, M + PAD, y + descTop + i * LH));
  y += descH;

  // ==================== TRIBUTAÇÃO MUNICIPAL (ISSQN) ====================
  blockTitle('TRIBUTAÇÃO MUNICIPAL (ISSQN)', ROW);
  row(
    ROW,
    [
      { l: 'Tipo de Tributação do ISSQN', v: getTribIssqnLabel(nota.trib_issqn) },
      {
        l: 'Município / Sigla UF / País de Incidência do ISSQN',
        v: formatLocal(nota.local_incidencia, extras.c_loc_incid, extras.c_pais_result),
        span: 2,
      },
    ],
    1,
  );
  if (temRegimes) {
    row(ROW, [
      {
        l: 'Regime Especial de Tributação do ISSQN',
        v: getRegimeEspecialLabel(extras.reg_esp_trib),
      },
      { l: 'Tipo de Imunidade do ISSQN', v: getImunidadeLabel(extras.tp_imunidade) },
      { l: 'Suspensão da Exigibilidade do ISSQN', v: getSuspensaoLabel(extras.tp_susp) },
      { l: 'Número Processo Suspensão', v: extras.n_processo },
    ]);
  }
  if (temDeducoes) {
    row(ROW, [
      { l: 'Benefício Municipal', v: getBeneficioMunicipalLabel(extras.tp_bm) },
      { l: 'Cálculo do BM', v: formatCurrencyXml(calculoBm) },
      { l: 'Total Deduções/Reduções', v: formatCurrency(nota.valor_calc_dr) },
      { l: 'Desconto Incondicionado', v: formatCurrencyXml(extras.v_desc_incond) },
    ]);
  }
  row(ROW, [
    { l: 'BC ISSQN', v: formatCurrency(nota.valor_bc) },
    { l: 'Alíquota Aplicada', v: formatPercent(nota.aliquota_aplic) },
    { l: 'Retenção do ISSQN', v: getTipoRetencaoIssqnLabel(nota.tp_ret_issqn) },
    { l: 'ISSQN Apurado', v: formatCurrency(nota.valor_issqn) },
  ]);

  // ==================== TRIBUTAÇÃO FEDERAL ====================
  blockTitle('TRIBUTAÇÃO FEDERAL (EXCETO CBS)', ROW);
  row(
    ROW,
    [
      { l: 'IRRF', v: formatCurrency(nota.v_ret_irrf || nota.irrf) },
      {
        l: 'Contribuição Previdenciária - Retida',
        v: formatCurrency(nota.v_ret_inss || nota.inss),
      },
      { l: 'Contribuições Sociais – Retidas', v: formatCurrency(nota.v_ret_csll || nota.csll) },
    ],
    1,
  );
  // Linha impressa só para competências até o fim de 2026 (item 2.4.5, nota 6)
  if (temPisCofins) {
    row(ROW, [
      { l: 'PIS - Débito Apuração Própria', v: formatCurrency(nota.v_pis || nota.pis) },
      { l: 'COFINS - Débito Apuração Própria', v: formatCurrency(nota.v_cofins || nota.cofins) },
      {
        l: 'Descrição Contrib. Sociais – Retidas',
        v: getRetPisCofinsDescricao(nota.tp_ret_pis_cofins),
        span: 2,
      },
    ]);
  }

  // ==================== TRIBUTAÇÃO IBS / CBS ====================
  // Notas sem o grupo IBSCBS (ou extraídas antes de o extrator gravá-lo) saem com traços
  const ibs = extras.ibscbs || {};
  const numero = (valor: string | number | undefined | null) => Number(valor) || 0;
  // Exclusões e reduções: somatório fixado pela NT (item 2.4.5)
  const exclusoesBc = ibs.v_bc
    ? formatCurrency(
        numero(extras.v_desc_incond) +
          numero(ibs.v_calc_ree_rep_res) +
          numero(nota.valor_issqn) +
          numero(nota.v_pis) +
          numero(nota.v_cofins),
      )
    : '-';
  const totalIbsCbs =
    ibs.v_ibs_tot || ibs.v_cbs ? formatCurrency(numero(ibs.v_ibs_tot) + numero(ibs.v_cbs)) : '-';

  blockTitle('TRIBUTAÇÃO IBS / CBS', ROW);
  row(
    ROW,
    [
      { l: 'CST / cClassTrib', v: joinCampos([ibs.cst || '-', ibs.c_class_trib || '-']) },
      {
        l: 'Indicador de Operação / Código IBGE Incidência / Município Incidência / Sigla UF',
        v: joinCampos([
          ibs.c_ind_op || '-',
          ibs.c_localidade_incid || '-',
          ibs.x_localidade_incid || '-',
          UF_POR_CODIGO_IBGE[(ibs.c_localidade_incid || '').slice(0, 2)] || '-',
        ]),
        span: 2,
      },
    ],
    1,
  );
  row(ROW, [
    { l: 'Exclusões e Reduções da Base de Cálculo', v: exclusoesBc },
    { l: 'Base de Cálculo Após Exclusões e Reduções', v: formatCurrencyXml(ibs.v_bc) },
    {
      l: 'Red. Alíquota IBS / Red. Alíquota CBS',
      v: joinCampos([
        formatPercentXml(ibs.p_red_aliq_uf),
        formatPercentXml(ibs.p_red_aliq_mun),
        formatPercentXml(ibs.p_red_aliq_cbs),
      ]),
    },
    {
      l: 'Alíquota – IBS UF / IBS Mun',
      v: joinCampos([formatPercentXml(ibs.p_ibs_uf), formatPercentXml(ibs.p_ibs_mun)]),
    },
  ]);
  row(ROW, [
    { l: 'Alíq. Efetiva Municipal – IBS', v: formatPercentXml(ibs.p_aliq_efet_mun) },
    { l: 'Valor Apurado Municipal – IBS', v: formatCurrencyXml(ibs.v_ibs_mun) },
    { l: 'Alíq. Efetiva Estadual – IBS', v: formatPercentXml(ibs.p_aliq_efet_uf) },
    { l: 'Valor Apurado Estadual – IBS', v: formatCurrencyXml(ibs.v_ibs_uf) },
  ]);
  row(ROW, [
    { l: 'Valor Total Apurado – IBS', v: formatCurrencyXml(ibs.v_ibs_tot) },
    { l: 'Alíquota - CBS', v: formatPercentXml(ibs.p_cbs) },
    { l: 'Alíquota Efetiva – CBS', v: formatPercentXml(ibs.p_aliq_efet_cbs) },
    { l: 'Valor Total Apurado – CBS', v: formatCurrencyXml(ibs.v_cbs) },
  ]);

  // ==================== VALOR TOTAL ====================
  const valorLiquido = nota.valor_liq || nota.valor_serv_prest;
  blockTitle('VALOR TOTAL DA NFS-E', ROW_ID);
  row(
    ROW_ID,
    [
      {
        l: 'VALOR DA OPERAÇÃO / SERVIÇO',
        v: formatCurrency(nota.valor_serv_prest),
        caps: true,
      },
      { l: 'Desconto Incondicionado', v: formatCurrencyXml(extras.v_desc_incond) },
      { l: 'Desconto Condicionado', v: formatCurrencyXml(extras.v_desc_cond) },
    ],
    1,
  );
  row(ROW_ID, [
    { l: 'Total das Retenções (ISSQN / Federais)', v: formatCurrency(nota.valor_total_ret) },
    { l: 'VALOR LÍQUIDO DA NFS-e', v: formatCurrency(valorLiquido), caps: true },
    { l: 'Total do IBS/CBS', v: totalIbsCbs },
    {
      l: 'VALOR LÍQUIDO DA NFS-e + IBS/CBS',
      v: ibs.v_tot_nf ? formatCurrencyXml(ibs.v_tot_nf) : formatCurrency(valorLiquido),
      caps: true,
      shade: true,
    },
  ]);

  // ==================== INFORMAÇÕES COMPLEMENTARES ====================
  blockTitle('INFORMAÇÕES COMPLEMENTARES', infoTitleH);
  y += infoTitleH;
  doc.setFont(fText, 'normal');
  doc.setFontSize(7);
  // A linha dos totais aproximados dos tributos é fixa: o corte só atinge o texto livre
  const infoMax = Math.floor((infoH - 3 - 0.6) / LH);
  const infoLines = [...clip(infoLinesAll, infoMax), totaisTributos];
  infoLines.forEach((line, i) => doc.text(line, M + PAD, y + 3 + i * LH));

  // Borda da página (1pt)
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.353);
  doc.rect(M, M, W, BOTTOM - M);

  const fileName = `${nota.chave_nfse || nota.numero_nfse || nota.id}.pdf`;
  return { doc, fileName };
};

// Gera o DANFSe e dispara o download (exportação individual)
export const generateNfsePdf = async (nota: INfse): Promise<void> => {
  const { doc, fileName } = await renderDanfseDocument(nota);
  doc.save(fileName);
};

// Gera o DANFSe e retorna como Blob (para uso no ZIP / exportação em lote)
export const generateNfsePdfBlob = async (
  nota: INfse,
): Promise<{ blob: Blob; fileName: string }> => {
  const { doc, fileName } = await renderDanfseDocument(nota);
  const blob = doc.output('blob');
  return { blob, fileName };
};

// Exporta múltiplas notas em um arquivo ZIP (um DANFSe por nota)
export const exportNfsesToZip = async (
  notas: INfse[],
  zipFileName?: string,
  // eslint-disable-next-line no-unused-vars
  onProgress?: (current: number, total: number) => void,
): Promise<void> => {
  if (notas.length === 0) {
    throw new Error('Nenhuma nota para exportar');
  }

  // Pré-carrega a logo antes do loop para cachear
  await getNfseLogo();

  const zip = new JSZip();
  const total = notas.length;

  for (let i = 0; i < notas.length; i += 1) {
    const nota = notas[i];
    // eslint-disable-next-line no-await-in-loop
    const { blob, fileName } = await generateNfsePdfBlob(nota);
    zip.file(fileName, blob);

    if (onProgress) {
      onProgress(i + 1, total);
    }
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });

  const defaultFileName = zipFileName || `NFSe_Lote_${new Date().toISOString().split('T')[0]}.zip`;
  const url = URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = defaultFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default generateNfsePdf;
